export type PrLocale = 'de' | 'en';

export type PrSectionLabels = {
  investmentStyle: string;
  pricesHeader: string;
  nav: string;
  fundVolume: string;
  minInvestment: string;
  bloomberg: string;
  conditions: string;
  issueSurcharge: string;
  redemptionFee: string;
  advisoryFee: string;
  ongoingCosts: string;
  performanceFee: string;
  masterData: string;
  isinWkn: string;
  fundType: string;
  investmentUniverse: string;
  inceptionDate: string;
  managementCompany: string;
  depositary: string;
  investmentAdviser: string;
  assetStructure: string;
  countryBreakdown: string;
  performance: string;
  fundColumn: string;
  equityWorldColumn: string;
  germanBenchmarkColumn: string;
  perfOneMonth: string;
  perfYtd: string;
  perf5y: string;
  perf10y: string;
  perfSinceInception: string;
  totalReturn: string;
  volatilitySinceInception: string;
  top5: string;
  chartSource: string;
  benchmarkFootnotes: string[];
};

const LABELS_DE: PrSectionLabels = {
  investmentStyle: 'Investmentstil',
  pricesHeader: 'Preise per',
  nav: 'NAV',
  fundVolume: 'Fondsvolumen',
  minInvestment: 'Minimum Investment (Erst-/Folge-)',
  bloomberg: 'Bloomberg',
  conditions: 'Konditionen',
  issueSurcharge: 'Ausgabeaufschlag',
  redemptionFee: 'Rücknahmeabschlag',
  advisoryFee: 'Beratungsvergütung',
  ongoingCosts: 'Kosten der Fondsverwaltung',
  performanceFee: 'Performance Gebühr',
  masterData: 'Stammdaten',
  isinWkn: 'ISIN / WKN',
  fundType: 'Fondsart',
  investmentUniverse: 'Anlageregion',
  inceptionDate: 'Auflagedatum',
  managementCompany: 'Verwaltungsgesellschaft und Alternativer Investmentfonds Manager',
  depositary: 'Verwahrstelle',
  investmentAdviser: 'Anlageberater',
  assetStructure: 'Anlagestruktur per',
  countryBreakdown: 'Aktien nach Ländern per',
  performance: 'Performance per',
  fundColumn: 'Fonds',
  equityWorldColumn: 'Aktien-Welt-Index¹',
  germanBenchmarkColumn: 'Deutscher Leit-Index²',
  perfOneMonth: 'Ein Monat',
  perfYtd: 'Laufendes Jahr',
  perf5y: 'Annualisierte Rendite der letzten 5 Jahre',
  perf10y: 'Annualisierte Rendite der letzten 10 Jahre',
  perfSinceInception: 'Annualisierte Rendite seit Auflage',
  totalReturn: 'Gesamtrendite',
  volatilitySinceInception: 'Volatilität seit Auflage',
  top5: 'Größte fünf Positionen per',
  chartSource:
    'Quelle: Depotbank. Der Chart berücksichtigt nur Monatsend-Daten und zeigt die reinvestierte Wertentwicklung der Anteilklasse P, indexiert auf 100 % zum Auflagedatum.',
  benchmarkFootnotes: [
    '¹ Referenzprodukt in EURO: ISIN FR0010315770 (inklusive aller Ausschüttungen).',
    '² Referenzprodukt in EURO: ISIN DE0008469000.',
  ],
};

const LABELS_EN: PrSectionLabels = {
  investmentStyle: 'Investment Style',
  pricesHeader: 'Fund Data as of',
  nav: 'Net Asset Value',
  fundVolume: 'Assets Under Management',
  minInvestment: 'Minimum Investment (Initial/Subsequent)',
  bloomberg: 'Bloomberg',
  conditions: 'Fees',
  issueSurcharge: 'Initial Sales Charge',
  redemptionFee: 'Redemption Fee',
  advisoryFee: 'Advisory Fee',
  ongoingCosts: 'Fund Administration Costs',
  performanceFee: 'Performance Fee',
  masterData: 'Key Information',
  isinWkn: 'ISIN / WKN',
  fundType: 'Fund Type',
  investmentUniverse: 'Investment Universe',
  inceptionDate: 'Inception Date',
  managementCompany: 'Management Company and Alternative Investment Fund Manager („AIFM")',
  depositary: 'Depositary',
  investmentAdviser: 'Investment Adviser',
  assetStructure: 'Portfolio as of',
  countryBreakdown: 'Country Breakdown as of',
  performance: 'Performance as of',
  fundColumn: 'Fund',
  equityWorldColumn: 'Equity World Index¹',
  germanBenchmarkColumn: 'German Benchmark Index²',
  perfOneMonth: 'One Month',
  perfYtd: 'Year to Date',
  perf5y: 'Compound Annual Growth Rate over 5 years',
  perf10y: 'Compound Annual Growth Rate over 10 years',
  perfSinceInception: 'Compound Annual Growth Rate since Inception',
  totalReturn: 'Total return',
  volatilitySinceInception: 'Volatility since Inception',
  top5: 'Top 5 Positions as of',
  chartSource:
    'Source: Custodian Bank. The chart only considers month-end data and shows the reinvested performance of share class P, indexed to 100 % at inception.',
  benchmarkFootnotes: [
    '¹ Reference product in EURO: ISIN FR0010315770 (including all distributions).',
    '² Reference product in EURO: ISIN DE0008469000.',
  ],
};

export function getPrSectionLabels(locale: PrLocale): PrSectionLabels {
  return locale === 'en' ? LABELS_EN : LABELS_DE;
}

export function getPrKonditionenDefaults(shareClass: string, locale: PrLocale) {
  if (shareClass === 'P') {
    return locale === 'de'
      ? {
          issueSurcharge: 'bis zu 3,0 % über Banken,\nkostenfrei für Registerkonten',
          redemptionFee: 'keiner',
          advisoryFee: '1,00 % p.a.',
          ongoingCostsNote: '1,41 % p.a. (Laufende Kosten\ninklusive der Beratungsvergütung)',
          performanceFee: '15 % p.a. (High Watermark und 5%\nHurdle)',
        }
      : {
          issueSurcharge: 'up to 3.0 %, nil for\nregistered accounts',
          redemptionFee: 'none',
          advisoryFee: '1.00 % p.a.',
          ongoingCostsNote: '1.41 % p.a. (Ongoing Costs including\nAdministration Fee)',
          performanceFee: '15 % p.a. (High Watermark and 5%\nHurdle)',
        };
  }
  return locale === 'de'
    ? {
        issueSurcharge: 'bis zu 5,0 % über Banken,\nkostenfrei für Registerkonten',
        redemptionFee: 'keiner',
        advisoryFee: 'bis zu 1,70 % p.a.',
        ongoingCostsNote: '1,99 % p.a. (Laufende Kosten\ninklusive der Beratungsvergütung)',
        performanceFee: '15 % p.a. (High Watermark und 5%\nHurdle)',
      }
    : {
        issueSurcharge: 'up to 5.0 %, nil for\nregistered accounts',
        redemptionFee: 'none',
        advisoryFee: 'up to 1.70 % p.a.',
        ongoingCostsNote: '1.99 % p.a. (Ongoing Costs including\nAdministration Fee)',
        performanceFee: '15 % p.a. (High Watermark and 5%\nHurdle)',
      };
}

export function getPrStammdatenDefaults(locale: PrLocale) {
  return locale === 'de'
    ? {
        fundType: 'Alternativer Investment Fonds, aktiv gemanagt, keine Benchmark Orientierung',
        investmentUniverse: 'global',
        managementCompany: 'Axxion S.A.',
        depositary: 'Banque de Luxembourg S.A.',
        investmentAdviser: 'P&R Investment Management Limited',
      }
    : {
        fundType: 'Alternative Investment Fund, actively managed, no benchmark orientation',
        investmentUniverse: 'global',
        managementCompany: 'Axxion S.A.',
        depositary: 'Banque de Luxembourg S.A.',
        investmentAdviser: 'P&R Investment Management Limited',
      };
}
